import React, { useMemo, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./ProductInstalled.css";

export default function CompositionInteractive({
  title,
  subtitle,
  data = [],
  image,
}) {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const chartRef = useRef(null);

  // ✅ Normalización defensiva: si viene null/number/string se convierte en objeto válido
  const safeData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((d, i) => {
      if (d == null) {
        return { label: `Parte ${i + 1}`, value: 0, description: "" };
      }
      if (typeof d === "number") {
        return { label: `Parte ${i + 1}`, value: d, description: "" };
      }
      return {
        label: d.label ?? `Parte ${i + 1}`,
        value: Number.isFinite(d.value) ? d.value : 0,
        description: d.description ?? "",
      };
    });
  }, [data]);

  const total = useMemo(
    () => safeData.reduce((a, d) => a + (d.value || 0), 0) || 1,
    [safeData]
  );

  const segments = useMemo(() => {
    let acc = -Math.PI / 2;
    return safeData.map((d) => {
      const angle = ((d.value || 0) / total) * Math.PI * 2;
      const seg = { start: acc, end: acc + angle };
      acc += angle;
      return seg;
    });
  }, [safeData, total]);

  const VB = 520;
  const cx = VB / 2, cy = VB / 2;
  const outerR = 200, innerR = 110;

  const colorFor = (i) => {
    const palette = [
      "#5A8DEE",
      "#7BA4F1",
      "#9DBBF4",
      "#FF6B35",
      "#283D57"
    ];
    return palette[i % palette.length];
  };

  const arcPath = (r1, r2, start, end) => {
    const large = end - start > Math.PI ? 1 : 0;
    const x1 = cx + r1 * Math.cos(start), y1 = cy + r1 * Math.sin(start);
    const x2 = cx + r1 * Math.cos(end),   y2 = cy + r1 * Math.sin(end);
    const x3 = cx + r2 * Math.cos(end),   y3 = cy + r2 * Math.sin(end);
    const x4 = cx + r2 * Math.cos(start), y4 = cy + r2 * Math.sin(start);
    return `M ${x1} ${y1} A ${r1} ${r1} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${r2} ${r2} 0 ${large} 0 ${x4} ${y4} Z`;
  };

  const handleMove = (e) => {
    const el = chartRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const mx = (e.clientX - rect.left) / rect.width;
    const my = (e.clientY - rect.top) / rect.height;
    const dx = (mx - 0.5) * 8;
    const dy = (my - 0.5) * 8;
    el.style.setProperty("--img-dx", `${dx}px`);
    el.style.setProperty("--img-dy", `${dy}px`);
  };

  useEffect(() => {
    const live = document.getElementById("ci-live");
    if (live && safeData[active]) {
      live.textContent = `${safeData[active].label}, ${safeData[active].value}%`;
    }
  }, [active, safeData]);

  if (!safeData.length) return null;

  const imageSrc = image || "";
  const titleText = title || t("composition.title", "Composición del material");
  const subtitleText = subtitle || t("composition.subtitleGeneric", "Explora la composición del producto");

  return (
    <section className="ci ci--premium">
      <div className="ci-left">
        <h3 className="ci-title--premium">{titleText}</h3>
        <p className="ci-subtitle--premium">{subtitleText}</p>
        <ul className="ci-items--premium">
          {safeData.map((d, i) => (
            <li
              key={i}
              className={`ci-item--premium ${active === i ? "is-active" : ""}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => setActive(i)}
              onFocus={() => setActive(i)}
              tabIndex={0}
            >
              <span className="ci-dot--premium" style={{ background: colorFor(i) }} />
              <div className="ci-item-content--premium">
                <div className="ci-item-head--premium">
                  <strong>{d.label}</strong>
                  <b className="ci-percent--premium">{d.value}%</b>
                </div>
                {d.description && <p className="ci-desc--premium">{d.description}</p>}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div
        className="ci-chart--premium"
        ref={chartRef}
        onMouseMove={handleMove}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        role="img"
        aria-label={t("composition.chartAria", "Distribución porcentual con imagen del producto")}
      >
        <svg
          viewBox={`0 0 ${VB} ${VB}`}
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            {segments.map((s, i) => (
              <clipPath key={i} id={`segClip-${i}`}>
                <path d={arcPath(outerR, innerR, s.start, s.end)} />
              </clipPath>
            ))}
            <filter id="activeGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <linearGradient id="centerGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.8)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0.4)" />
            </linearGradient>
          </defs>

          {/* Imagen dinámica del producto */}
          <image
            href={imageSrc}
            x={0}
            y={0}
            width={VB}
            height={VB}
            preserveAspectRatio="xMidYMid meet"
            style={{
              opacity: 0.9,
              pointerEvents: "none",
              transition: "transform 0.3s ease",
              transform: isHovering ? "scale(1.05)" : "scale(1)",
            }}
          />

          {segments.map((s, i) => (
            <g key={`seg-${i}`} clipPath={`url(#segClip-${i})`}>
              <path
                d={arcPath(outerR, innerR, s.start, s.end)}
                fill={colorFor(i)}
                style={{
                  mixBlendMode: "multiply",
                  opacity: active === i ? 0.35 : 0.15,
                  transition: "opacity .3s ease",
                  pointerEvents: "none",
                }}
              />
            </g>
          ))}

          {segments.map((s, i) => (
            <path
              key={`hit-${i}`}
              d={arcPath(outerR, innerR, s.start, s.end)}
              fill="transparent"
              stroke={active === i ? "rgba(255,255,255,0.6)" : "transparent"}
              strokeWidth={active === i ? 3 : 0}
              style={{
                transition: "all .3s ease",
                filter: active === i ? "url(#activeGlow)" : "none",
                cursor: "pointer",
              }}
              onMouseEnter={() => setActive(i)}
              onClick={() => setActive(i)}
            />
          ))}

          <circle cx={cx} cy={cy} r={innerR - 10} fill="url(#centerGradient)" opacity="0.3" />

          <text x={cx} y={cy} textAnchor="middle" className="ci-center">
            <tspan className="ci-center-percent" x={cx} dy="-10">
              {safeData[active]?.value ?? 0}%
            </tspan>
            <tspan className="ci-center-name" x={cx} dy="26">
              {safeData[active]?.label ?? ""}
            </tspan>
          </text>
        </svg>
        <span id="ci-live" className="sr-only" aria-live="polite" />
      </div>
    </section>
  );
}
